import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Confetti } from "@/components/Confetti";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const tries = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const s = await api.paymentStatus(sessionId);
        if (s.payment_status === "paid") {
          await refreshUser();
          if (!stop) setState("paid");
          return;
        }
        if (["failed", "expired"].includes(s.payment_status)) { if (!stop) setState("failed"); return; }
      } catch (_) {}
      tries.current += 1;
      if (tries.current > 12) { if (!stop) setState("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, refreshUser]);

  return (
    <div className="relative mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-4 text-center">
      {state === "paid" && <Confetti />}
      {state === "checking" && (
        <>
          <Loader2 size={48} className="animate-spin text-[#0D9488]" />
          <h1 className="mt-6 font-heading text-2xl font-bold text-[#1F2937]">Confirming your subscription…</h1>
          <p className="mt-2 font-body text-[#6B7280]">This only takes a moment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}
            className="flex h-24 w-24 items-center justify-center rounded-full bg-[#10B981] text-white shadow-lg"><CheckCircle2 size={48} /></motion.div>
          <h1 className="mt-6 font-heading text-3xl font-extrabold text-[#1F2937]">You're all set! 🎉</h1>
          <p className="mt-2 font-body text-lg text-[#6B7280]">Your subscription is active. The full PreK–8 curriculum is unlocked.</p>
          <button onClick={() => navigate("/")} data-testid="success-continue"
            className="mt-8 rounded-full bg-[#0D9488] px-8 py-3 font-heading font-bold text-white hover:bg-[#0B7F74]">Go to my dashboard</button>
        </>
      )}
      {state === "failed" && (
        <>
          <XCircle size={48} className="text-[#E05A47]" />
          <h1 className="mt-6 font-heading text-2xl font-bold text-[#1F2937]">We couldn't confirm the payment</h1>
          <p className="mt-2 font-body text-[#6B7280]">If you completed checkout, it may take a moment. Otherwise, try again.</p>
          <Link to="/pricing" data-testid="success-retry" className="mt-8 rounded-full bg-[#0D9488] px-8 py-3 font-heading font-bold text-white hover:bg-[#0B7F74]">Back to pricing</Link>
        </>
      )}
    </div>
  );
}
